import { useMemo, useState } from "react";
import MultiPageSketchPad from "@/components/sketch/MultiPageSketchPad";
import { SKETCH_TEMPLATE_LABELS } from "@/components/sketch/SketchTemplateUnderlay";

const SCORE_OPTIONS = [
  { value: 5, label: "5 Excellent" },
  { value: 4, label: "4 Good" },
  { value: 3, label: "3 Developing" },
  { value: 2, label: "2 Practice" },
  { value: 1, label: "1 Support" },
];

function emptyTaskState(tasks) {
  const out = {};
  for (const t of tasks || []) {
    out[t.task_id] = { score: null, note: "" };
  }
  return out;
}

function assessmentsFromState(taskState, tasks) {
  return (tasks || []).map((t) => ({
    task_id: t.task_id,
    score: taskState[t.task_id]?.score ?? null,
    note: (taskState[t.task_id]?.note || "").trim(),
  }));
}

function stateFromAssessments(assessments, tasks) {
  const base = emptyTaskState(tasks);
  for (const a of assessments || []) {
    if (a?.task_id && base[a.task_id]) {
      base[a.task_id] = {
        score: a.score == null ? null : Number(a.score),
        note: a.note || "",
      };
    }
  }
  return base;
}

function emptyCustomCategoryState(categories) {
  const out = {};
  for (const c of categories || []) {
    out[c.id] = { score: null, private_note: "" };
  }
  return out;
}

function customAssessmentsFromState(categoryState, categories) {
  return (categories || []).map((c) => ({
    category_id: c.id,
    score: categoryState[c.id]?.score ?? null,
    private_note: (categoryState[c.id]?.private_note || "").trim(),
  }));
}

function stateFromCustomAssessments(assessments, categories) {
  const base = emptyCustomCategoryState(categories);
  for (const a of assessments || []) {
    if (a?.category_id && base[a.category_id]) {
      base[a.category_id] = {
        score: a.score == null ? null : Number(a.score),
        private_note: a.private_note || a.note || "",
      };
    }
  }
  return base;
}

function computeProgressScore(taskState) {
  const scores = Object.values(taskState)
    .map((v) => v.score)
    .filter((s) => typeof s === "number" && s >= 1 && s <= 5);
  if (!scores.length) return null;
  return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100;
}

function CompactNoteField({ taskId, label, value, onChange, disabled, expanded, onToggle }) {
  if (!expanded && !value) {
    return (
      <button
        type="button"
        className={`audit-note-toggle ${value ? "has-note" : ""}`}
        onClick={onToggle}
        disabled={disabled}
        data-testid={`qdf-note-toggle-${taskId}`}
      >
        + Add note
      </button>
    );
  }
  return (
    <div className="w-full mt-2">
      {!expanded && value ? (
        <button type="button" className="audit-note-toggle has-note" onClick={onToggle} disabled={disabled}>
          Edit note
        </button>
      ) : null}
      {(expanded || value) && (
        <input
          type="text"
          className="w-full min-h-11 px-3 py-2 border rounded text-sm"
          style={{ borderColor: "rgba(22,25,28,.24)" }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Short coaching note"
          aria-label={`Optional note for ${label}`}
          data-testid={`qdf-note-${taskId}`}
        />
      )}
    </div>
  );
}

export default function QuickDailyFeedbackPanel({
  tasks,
  scoreLabels,
  taskState,
  setTaskState,
  customCategories,
  customCategoryState,
  setCustomCategoryState,
  form,
  onChange,
  setForm,
  hasLearner,
  practicalEnabled,
  autosaveStatus,
  onManualSave,
  onGenerateFeedback,
  onApprovePublish,
  busy,
  dailyProgressScore,
  onSketchSave,
  sketchSaving,
  sketchError,
  sketches,
  inlineSketchPages = null,
  inlineSketchStrokesJson = "",
  inlineSketchKey = "new",
  sketchSaveRevision = null,
  onEditSketch,
  aiDraft,
  makeLatestSketchVisible,
  onMakeLatestSketchVisible,
  variant = "default",
  part = "all",
  focusLabels = [],
}) {
  const controlsEnabled = hasLearner && practicalEnabled;
  const isAudit = variant === "audit";
  const [expandedNotes, setExpandedNotes] = useState({});

  const setScore = (taskId, score) => {
    if (!controlsEnabled) return;
    setTaskState((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], score },
    }));
  };

  const setNote = (taskId, note) => {
    if (!hasLearner) return;
    setTaskState((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], note },
    }));
  };

  const setCustomScore = (categoryId, score) => {
    if (!controlsEnabled || !setCustomCategoryState) return;
    setCustomCategoryState((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], score },
    }));
  };

  const setCustomNote = (categoryId, private_note) => {
    if (!controlsEnabled || !setCustomCategoryState) return;
    setCustomCategoryState((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], private_note },
    }));
  };

  const assessedCount = useMemo(
    () => Object.values(taskState).filter((v) => typeof v.score === "number").length,
    [taskState]
  );

  const toggleNote = (taskId) => {
    setExpandedNotes((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const sketchBlock = (
    <div className={isAudit ? "audit-sketchbook audit-surface" : "border rounded p-3 space-y-2 bg-muted/20"} data-testid="qdf-sketch-inline">
      {isAudit && (
        <div className="audit-sketchbook-header">
          <h3 className="audit-sketchbook-title">Sketchbook</h3>
        </div>
      )}
      {!isAudit && <h3 className="text-sm font-semibold">Freehand sketch (optional)</h3>}
      {!isAudit && (
        <p className="text-xs text-muted-foreground">
          Draw roundabouts, lane position, merges, or parking. Use finger, Apple Pencil, or mouse.
        </p>
      )}
      {sketchError && (
        <p role="alert" className="text-destructive text-sm" data-testid="tr-sketch-error">
          {sketchError}
        </p>
      )}
      {sketchSaveRevision != null && !sketchSaving && !sketchError && (
        <p className="text-xs text-muted-foreground" data-testid="sketch-save-confirmed">
          Sketch saved · server confirmed · revision {sketchSaveRevision}
        </p>
      )}
      <MultiPageSketchPad
        key={`qdf-inline-sketch-${inlineSketchKey}`}
        initialPages={inlineSketchPages}
        initialStrokesJson={inlineSketchStrokesJson}
        readOnly={!controlsEnabled}
        saving={sketchSaving}
        saveLabel="Save sketch"
        onSave={onSketchSave}
        variant={isAudit ? "audit" : "default"}
      />
      {sketches?.length > 0 && (
        <ul className="text-sm space-y-1" data-testid="qdf-sketches-list">
          {sketches.map((s) => (
            <li key={s.sketch_id}>
              {s.title || "Sketch"} · {s.page_count || 1} page{(s.page_count || 1) === 1 ? "" : "s"} ·{" "}
              {SKETCH_TEMPLATE_LABELS[s.template_id] || s.template_id}
              {(s.strokes_json || "").length > 10 ? " · drawing saved" : ""}
              <button type="button" className="ml-2 underline min-h-11" onClick={() => onEditSketch(s)}>
                Edit
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const assessmentSection = (
    <>
      <div className="flex flex-wrap items-center gap-3 text-sm mb-3" data-testid="qdf-progress-score">
        <span className="font-medium">Daily Progress Score (training only):</span>
        <span data-testid="qdf-score-value">{dailyProgressScore != null ? `${dailyProgressScore} / 5` : "—"}</span>
        <span style={{ color: "rgba(22,25,28,.6)" }}>
          ({assessedCount} task{assessedCount === 1 ? "" : "s"} scored)
        </span>
      </div>

      <fieldset className="space-y-1 mb-3" data-testid="qdf-share-score">
        <legend className="text-sm font-medium">Share score with learner</legend>
        <div className="flex flex-wrap gap-3">
          {[
            ["HIDDEN", "No — hide numerical score"],
            ["VISIBLE", "Yes — learner may see score"],
          ].map(([val, label]) => (
            <label key={val} className="flex items-center gap-2 min-h-11 cursor-pointer">
              <input
                type="radio"
                name="score_visibility"
                value={val}
                checked={(form.score_visibility || "HIDDEN") === val}
                onChange={onChange}
                disabled={!controlsEnabled}
              />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div data-testid="qdf-task-list">
        {(tasks || []).map((task) => {
          const row = taskState[task.task_id] || { score: null, note: "" };
          const isFocus = focusLabels.some((fl) => task.label.includes(fl) || fl.includes(task.label));
          const noteOpen = expandedNotes[task.task_id] || !!row.note;

          if (isAudit) {
            return (
              <div
                key={task.task_id}
                className={`audit-score-row ${isFocus ? "is-focus" : ""}`}
                data-testid={`qdf-task-${task.task_id}`}
              >
                <div className="audit-score-label">{task.label}</div>
                <div className="audit-score-chips" role="group" aria-label={`Score for ${task.label}`}>
                  {SCORE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className="audit-score-chip"
                      data-selected={row.score === opt.value}
                      data-band={String(opt.value)}
                      aria-pressed={row.score === opt.value}
                      aria-label={`${task.label}: ${opt.label}`}
                      disabled={!controlsEnabled}
                      onClick={() => setScore(task.task_id, opt.value)}
                      data-testid={`qdf-score-${task.task_id}-${opt.value}`}
                    >
                      {opt.value}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="audit-score-chip"
                    data-selected={row.score == null}
                    data-band="na"
                    aria-pressed={row.score == null}
                    aria-label={`${task.label}: not covered today`}
                    disabled={!controlsEnabled}
                    onClick={() => setScore(task.task_id, null)}
                    data-testid={`qdf-score-${task.task_id}-na`}
                  >
                    N/A
                  </button>
                </div>
                <CompactNoteField
                  taskId={task.task_id}
                  label={task.label}
                  value={row.note}
                  onChange={(v) => setNote(task.task_id, v)}
                  disabled={!controlsEnabled}
                  expanded={noteOpen}
                  onToggle={() => toggleNote(task.task_id)}
                />
              </div>
            );
          }

          return (
            <li key={task.task_id} className="border rounded p-3 space-y-2 mb-3 list-none" data-testid={`qdf-task-${task.task_id}`}>
              <div className="font-medium text-sm">{task.label}</div>
              <div className="flex flex-wrap gap-2" role="group" aria-label={`Score for ${task.label}`}>
                {SCORE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`min-h-11 min-w-[3.25rem] px-2 py-2 rounded border text-sm ${
                      row.score === opt.value ? "btn-accent font-semibold" : "btn-outline"
                    }`}
                    aria-pressed={row.score === opt.value}
                    disabled={!controlsEnabled}
                    onClick={() => setScore(task.task_id, opt.value)}
                    data-testid={`qdf-score-${task.task_id}-${opt.value}`}
                  >
                    {opt.value}
                  </button>
                ))}
                <button
                  type="button"
                  className={`min-h-11 px-3 py-2 rounded border text-sm ${
                    row.score == null ? "bg-muted font-semibold" : "btn-outline"
                  }`}
                  aria-pressed={row.score == null}
                  disabled={!controlsEnabled}
                  onClick={() => setScore(task.task_id, null)}
                  data-testid={`qdf-score-${task.task_id}-na`}
                >
                  N/A
                </button>
              </div>
              <label className="block text-xs text-muted-foreground">
                Optional note
                <input
                  type="text"
                  className="mt-1 w-full min-h-11 px-3 py-2 border rounded text-sm"
                  value={row.note}
                  onChange={(e) => setNote(task.task_id, e.target.value)}
                  disabled={!controlsEnabled}
                  data-testid={`qdf-note-${task.task_id}`}
                />
              </label>
            </li>
          );
        })}
      </div>

      {(customCategories || []).length > 0 && (
        <div className="audit-custom-category-section" data-testid="qdf-custom-category-list">
          <div className="audit-panel-header">
            <div>
              <h3 className="audit-section-title">Your teaching categories</h3>
              <span className="audit-private-tag">Instructor only · never shown to the learner</span>
            </div>
          </div>
          {(customCategories || []).map((cat) => {
            const row = customCategoryState?.[cat.id] || { score: null, private_note: "" };
            const noteOpen = expandedNotes[`custom-${cat.id}`] || !!row.private_note;
            return (
              <div
                key={cat.id}
                className="audit-score-row"
                data-testid={`qdf-custom-${cat.id}`}
              >
                <div className="audit-score-label">{cat.label}</div>
                <div className="audit-score-chips" role="group" aria-label={`Score for ${cat.label}`}>
                  {SCORE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className="audit-score-chip"
                      data-selected={row.score === opt.value}
                      data-band={String(opt.value)}
                      aria-pressed={row.score === opt.value}
                      aria-label={`${cat.label}: ${opt.label}`}
                      disabled={!controlsEnabled}
                      onClick={() => setCustomScore(cat.id, opt.value)}
                      data-testid={`qdf-custom-score-${cat.id}-${opt.value}`}
                    >
                      {opt.value}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="audit-score-chip"
                    data-selected={row.score == null}
                    data-band="na"
                    aria-pressed={row.score == null}
                    aria-label={`${cat.label}: not covered today`}
                    disabled={!controlsEnabled}
                    onClick={() => setCustomScore(cat.id, null)}
                    data-testid={`qdf-custom-score-${cat.id}-na`}
                  >
                    N/A
                  </button>
                </div>
                <CompactNoteField
                  taskId={`custom-${cat.id}`}
                  label={cat.label}
                  value={row.private_note}
                  onChange={(v) => setCustomNote(cat.id, v)}
                  disabled={!controlsEnabled}
                  expanded={noteOpen}
                  onToggle={() => toggleNote(`custom-${cat.id}`)}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const feedbackSection = (
    <div className="space-y-3" data-testid="qdf-overall-notes">
      {isAudit && (
        <div className="audit-panel-header">
          <h3 className="audit-section-title">Lesson feedback</h3>
          <button
            type="button"
            className="audit-btn-compact"
            onClick={onGenerateFeedback}
            disabled={!controlsEnabled || busy}
            data-testid="qdf-generate-feedback-inline"
          >
            Generate AI draft
          </button>
        </div>
      )}
      <label className="block text-sm font-medium">
        Strengths
        <textarea
          name="strengths"
          className="mt-1 w-full min-h-[4rem] p-3 border rounded"
          style={isAudit ? { borderColor: "rgba(22,25,28,.24)" } : undefined}
          value={form.strengths}
          onChange={onChange}
          disabled={!controlsEnabled}
          rows={2}
          data-testid="tr-strengths"
        />
      </label>
      <label className="block text-sm font-medium">
        Practice areas
        <textarea
          name="areas_for_improvement"
          className="mt-1 w-full min-h-[4rem] p-3 border rounded"
          style={isAudit ? { borderColor: "rgba(22,25,28,.24)" } : undefined}
          value={form.areas_for_improvement}
          onChange={onChange}
          disabled={!controlsEnabled}
          rows={2}
          data-testid="tr-improve"
        />
      </label>
      <label className="block text-sm font-medium">
        Next lesson focus
        <textarea
          name="next_session_focus"
          className="mt-1 w-full min-h-[3rem] p-3 border rounded"
          style={isAudit ? { borderColor: "rgba(22,25,28,.24)" } : undefined}
          value={form.next_session_focus}
          onChange={onChange}
          disabled={!controlsEnabled}
          rows={2}
          data-testid="tr-next"
        />
      </label>

      {isAudit ? (
        <div className="audit-private-panel">
          <label htmlFor="qdf-private-note">
            Private — instructor only
            <span className="audit-private-tag">Instructor only · never shown to the learner</span>
          </label>
          <textarea
            id="qdf-private-note"
            name="instructor_notes_private"
            rows={2}
            placeholder="Not visible to the learner or their organisation"
            value={form.instructor_notes_private}
            onChange={onChange}
            disabled={!controlsEnabled}
            data-testid="qdf-private-note"
          />
        </div>
      ) : (
        <label className="block text-sm font-medium">
          Private instructor note (never learner-visible)
          <textarea
            name="instructor_notes_private"
            className="mt-1 w-full min-h-[3rem] p-3 border rounded"
            value={form.instructor_notes_private}
            onChange={onChange}
            disabled={!controlsEnabled}
            rows={2}
            data-testid="qdf-private-note"
          />
        </label>
      )}

      {aiDraft && (
        <div className={isAudit ? "audit-surface audit-section text-sm" : "border rounded p-3 space-y-1 bg-muted/40"} data-testid="ai-draft-box">
          <p className="font-medium">AI Draft ({aiDraft.status}) — review before publishing</p>
          <p className="text-sm whitespace-pre-wrap">{aiDraft.draft_text}</p>
        </div>
      )}

      {sketches?.length > 0 && (
        <label className="flex items-center gap-2 text-sm min-h-11">
          <input
            type="checkbox"
            checked={!!makeLatestSketchVisible}
            onChange={(e) => onMakeLatestSketchVisible?.(e.target.checked)}
            disabled={!controlsEnabled}
            data-testid="qdf-sketch-learner-visible"
          />
          <span>Make latest sketch learner-visible when published (default off)</span>
        </label>
      )}
    </div>
  );

  const actionsSection = (
    <div className="flex flex-wrap gap-2 items-center">
      <button type="button" className="btn-outline min-h-11" onClick={onGenerateFeedback} disabled={!controlsEnabled || busy} data-testid="qdf-generate-feedback">
        Generate AI Draft
      </button>
      <button type="button" className="btn-outline min-h-11" onClick={onManualSave} disabled={!controlsEnabled || busy} data-testid="qdf-manual-save">
        Save draft
      </button>
      <button type="button" className="btn-accent min-h-11" onClick={onApprovePublish} disabled={!controlsEnabled || busy} data-testid="qdf-approve-publish">
        Approve &amp; Publish
      </button>
      <span role="status" className="text-sm text-muted-foreground" data-testid="qdf-autosave-status">
        {autosaveStatus === "saving" && "Saving training draft…"}
        {autosaveStatus === "saved" && "Training draft saved"}
        {autosaveStatus === "retry" && "Training draft save failed — retrying"}
      </span>
    </div>
  );

  if (part === "assessment") {
    return (
      <section className={isAudit ? "audit-surface audit-section" : ""} aria-label="Today's assessment" data-testid="quick-daily-feedback-panel">
        {!hasLearner && (
          <p role="status" className="text-sm" data-testid="qdf-select-learner">
            Select a learner to start Quick Daily Feedback.
          </p>
        )}
        {hasLearner && !practicalEnabled && (
          <div className="audit-restricted-banner mb-3" data-testid="qdf-entitlement-blocked">
            Practical training is not enabled for this learner. Theory learning remains available. Practical controls
            stay disabled until training is authorised.
          </div>
        )}
        {isAudit && (
          <div className="audit-panel-header">
            <h2 className="audit-section-title">Today&apos;s assessment</h2>
          </div>
        )}
        {assessmentSection}
      </section>
    );
  }

  if (part === "feedback") {
    return (
      <section className={isAudit ? "audit-surface audit-section" : ""} data-testid="qdf-feedback-section">
        {feedbackSection}
      </section>
    );
  }

  if (part === "sketch") {
    return sketchBlock;
  }

  return (
    <div className="space-y-4" data-testid="quick-daily-feedback-panel">
      {!hasLearner && (
        <p role="status" className="text-sm font-medium border rounded p-3 bg-muted/40" data-testid="qdf-select-learner">
          Select a learner above to start Quick Daily Feedback.
        </p>
      )}
      {hasLearner && !practicalEnabled && (
        <p role="status" className="text-sm text-destructive border border-destructive/30 rounded p-3" data-testid="qdf-entitlement-blocked">
          Practical training is not enabled for this learner.
        </p>
      )}
      {!isAudit && <p className="text-sm text-muted-foreground">Score only what you covered today. N/A tasks are excluded from the lesson progress score.</p>}
      {assessmentSection}
      {!isAudit && sketchBlock}
      {feedbackSection}
      {!isAudit && actionsSection}
    </div>
  );
}

export {
  assessmentsFromState,
  computeProgressScore,
  customAssessmentsFromState,
  emptyCustomCategoryState,
  emptyTaskState,
  stateFromAssessments,
  stateFromCustomAssessments,
};
